/**
 * Support Requests Panel
 *
 * POST /client/support lets a client submit a support ticket, but no
 * staff-side route or UI ever existed to read or resolve them - real
 * client questions/complaints were unreachable by any human after
 * submission. This panel is the staff-side counterpart.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MessageSquareText, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved'];

const statusBadgeClass = (status) => {
  if (status === 'Resolved') return 'bg-green-500/20 text-green-300 border-green-500/30';
  if (status === 'In Progress') return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
  return 'bg-white/10 text-white/70 border-white/20';
};

const SupportRequestsPanel = ({ user }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/support-requests`, { headers: authHeaders() });
      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
      } else {
        toast.error('Failed to load support requests');
      }
    } catch (error) {
      toast.error('Error loading support requests');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const updateStatus = async (requestId, status) => {
    setUpdatingId(requestId);
    try {
      const response = await fetch(`${API}/support-requests/${requestId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ data: { status } }),
      });
      if (response.ok) {
        setRequests((prev) => prev.map((r) => (r.id === requestId ? { ...r, status } : r)));
        toast.success('Support request updated');
      } else {
        toast.error('Failed to update support request');
      }
    } catch (error) {
      toast.error('Error updating support request');
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return <p className="text-white/60 text-center py-8" data-testid="support-requests-loading">Loading support requests...</p>;
  }

  return (
    <div className="space-y-4" data-testid="support-requests-panel">
      {requests.length === 0 ? (
        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6 text-center py-8">
            <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-2" />
            <p className="text-white/60 text-sm">No support requests.</p>
          </CardContent>
        </Card>
      ) : (
        requests.map((req) => (
          <Card className="glass-panel border-white/10" key={req.id} data-testid={`support-request-${req.id}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white/90 flex items-center gap-2 text-sm">
                  <MessageSquareText size={16} className="text-[var(--gold)]" />
                  {req.subject || '(no subject)'}
                </CardTitle>
                <Badge className={statusBadgeClass(req.status)}>{req.status}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-white/80">{req.message}</p>
              <p className="text-xs text-white/50 mt-2">
                {req.created_by} • {req.created_at ? new Date(req.created_at).toLocaleString() : ''} • Priority: {req.priority || 'Medium'}
              </p>
              <div className="flex items-center gap-2 mt-3">
                {STATUS_OPTIONS.filter((s) => s !== req.status).map((status) => (
                  <Button
                    key={status}
                    className="ghost-btn small"
                    disabled={updatingId === req.id}
                    onClick={() => updateStatus(req.id, status)}
                    data-testid={`support-request-${req.id}-mark-${status.toLowerCase().replace(' ', '-')}`}
                  >
                    Mark {status}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default SupportRequestsPanel;
